import { Grid } from "@mui/material";
import Backdrop from "@mui/material/Backdrop";
import Fade from "@mui/material/Fade";
import Modal, { ModalProps } from "@mui/material/Modal";
import createStyles from "@mui/styles/createStyles";
import makeStyles from "@mui/styles/makeStyles";
import { OLButton } from "OLButton";
import { OLClearIcon } from "components/CustomIcons/OLClearIcon";
import { Tooltip } from "components/Tooltips/Tooltip";
import { mediaQuery, OLTheme } from "contexts/Theme/ThemeProvider";
import { useIsOverflown } from "hooks/useIsOverflown";
import React from "react";

const useStyles = makeStyles((theme: OLTheme) =>
  createStyles({
    header: {
      color: theme.gray800,
      fontSize: 20,
      fontWeight: "500",
      lineHeight: theme.spacing(12),
      margin: 0,
      overflow: "hidden",
      textOverflow: "ellipsis",
      textWrap: "nowrap",
      whiteSpace: "break-spaces",
    },
    headerWrapper: {
      display: "flex",
      justifyContent: "space-between",
    },
    headersWrapper: {
      marginBottom: theme.spacing(10),
    },
    subHeader: {
      color: theme.gray400,
      display: "block",
      fontSize: 14,
      fontWeight: 400,
      marginTop: theme.spacing(4),
      marginBottom: 0,
    },
    submitButton: {
      flexGrow: 1,
    },
    cancelButton: {
      flexGrow: 1,
    },
    buttonsContainer: {
      display: "flex",
      alignItems: "end",
      justifyContent: "stretch",
      flexWrap: "nowrap",
      gap: 32,
      marginTop: theme.spacing(12),
    },
    modal: {
      alignItems: "center",
      backgroundColor: `${theme.primary500}80`,
      display: "flex",
      justifyContent: "center",
      overflowY: "auto",
      [`@media (max-width: ${mediaQuery.mobile}px)`]: {
        display: "none",
      },
    },
    modalBg: ({
      modalWidth,
      allowOverflow,
    }: {
      modalWidth: number;
      allowOverflow?: boolean;
    }) => ({
      backgroundColor: theme.palette.background.paper,
      border: "none",
      display: "flex",
      flexDirection: "column",
      maxHeight: "95%",
      maxWidth: modalWidth ? "95%" : "30%",
      padding: theme.spacing(12),
      width: modalWidth || 454,
      zIndex: 1,
      borderRadius: "8px",
      overflowY: allowOverflow ? "visible" : "auto",
    }),
  }),
);

interface Props extends Omit<ModalProps, "children"> {
  clearIcon?: boolean;
  cancelText?: string;
  className?: string;
  children?: React.ReactElement;
  header?: string | JSX.Element;
  onClose: () => void;
  additionalCloseAction?: () => void;
  submitAction?: () => void;
  subHeader?: string;
  submitText?: string;
  modalWidth?: number;
  "data-test-id"?: string;
  submitDisabled?: boolean;
  allowOverflow?: boolean;
}

export function OLModal({
  cancelText,
  className,
  children,
  header,
  onClose,
  additionalCloseAction,
  open,
  subHeader,
  submitAction,
  submitText,
  modalWidth,
  clearIcon = true,
  submitDisabled,
  "data-test-id": dataTestId,
  disableEscapeKeyDown,
  allowOverflow,
}: Props) {
  const classes = useStyles({
    modalWidth: modalWidth || 0,
    allowOverflow: allowOverflow,
  });
  const { observeElement, isOverflown } = useIsOverflown();
  const isAnyButtonAdded = !!(submitText || cancelText);

  return (
    <Modal
      BackdropComponent={Backdrop}
      BackdropProps={{ timeout: 500 }}
      className={classes.modal}
      closeAfterTransition
      onClose={() => onClose()}
      open={open}
      data-test-id={dataTestId}
      disableEscapeKeyDown={disableEscapeKeyDown}
    >
      <Fade in={open}>
        <div
          className={`${classes.modalBg} ${className ? className : ""}`}
          style={{ width: `${modalWidth}` }}
          data-test-id={dataTestId && dataTestId}
        >
          {(header || subHeader) && (
            <div className={classes.headersWrapper}>
              <div className={classes.headerWrapper}>
                {header && (
                  <Tooltip text={header} disableHoverListener={!isOverflown}>
                    <h2 className={classes.header} ref={observeElement}>
                      {header}
                    </h2>
                  </Tooltip>
                )}
                {clearIcon && (
                  <OLClearIcon
                    data-test-id={dataTestId && `${dataTestId}-clear-icon`}
                    onClick={() => onClose()}
                  />
                )}
              </div>
              <h3 className={classes.subHeader}>{subHeader}</h3>
            </div>
          )}
          {children}
          {isAnyButtonAdded && (
            <Grid container className={classes.buttonsContainer}>
              {cancelText && (
                <OLButton
                  className={classes.cancelButton}
                  data-test-id={dataTestId && `${dataTestId}-cancel-button`}
                  onClick={() => {
                    onClose();
                    additionalCloseAction?.();
                  }}
                  variant="outlined"
                >
                  {cancelText}
                </OLButton>
              )}
              {submitText && (
                <OLButton
                  className={classes.submitButton}
                  color="primary"
                  data-test-id={dataTestId && `${dataTestId}-submit-button`}
                  onClick={() => submitAction?.()}
                  variant="contained"
                  disabled={submitDisabled}
                >
                  {submitText}
                </OLButton>
              )}
            </Grid>
          )}
        </div>
      </Fade>
    </Modal>
  );
}
